import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Copy, Eye, EyeOff, FileDown, Plus, Save, Send, Undo2, Upload, History, CircleDot, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PacketView } from "@/components/packet/PacketView";
import { SectionList, SectionEditor } from "@/components/packet/PacketSections";
import { PrepOverview } from "@/components/packet/PrepParts";

export const saveBlob = async (url, filename) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = href; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 4000);
  } catch { toast.error("Could not download the file"); }
};
export const uploadPacketFile = async (file) => {
  const fd = new FormData(); fd.append("file", file);
  const r = await api.post("/packets/files", fd);
  return { file_id: r.data.id, name: r.data.name, content_type: r.data.content_type };
};
const openFile = (f) => saveBlob(`/files/${f.file_id}`, f.name || "file");

function StatusPill({ packet }) {
  const map = { draft: ["Draft — not published", "bg-gray-100 text-gray-700"], published: [`Published · v${packet.version}`, "bg-teal-light text-teal-dark"], unpublished: ["Unpublished", "bg-amber-100 text-amber-800"] };
  const [label, cls] = map[packet.status] || map.draft;
  return <span data-testid="packet-status" className={`text-xs font-semibold rounded-full px-3 py-1 ${cls}`}>{label}</span>;
}

function CreatePacket({ pid, onCreated }) {
  const [lib, setLib] = useState([]);
  const [from, setFrom] = useState("");
  useEffect(() => { api.get("/packets/library").then((r) => setLib(r.data.filter((x) => x.production_id !== pid))).catch(() => {}); }, [pid]);
  const create = async (fromPid) => {
    try { await api.post(`/packets/${pid}`, { from_production_id: fromPid || null }); toast.success(fromPid ? "Packet copied — review and update it" : "Packet created with suggested sections"); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 sm:p-8 max-w-2xl" data-testid="packet-create">
      <BookOpen className="w-8 h-8 text-teal" />
      <h3 className="font-head text-xl font-bold mt-3">Create this recital's packet</h3>
      <p className="text-sm text-muted-foreground mt-1">Start with Studio V's suggested sections, or copy last recital's packet and update it. Nothing is visible to families until you publish.</p>
      <div className="flex flex-wrap gap-2 mt-5">
        <Button data-testid="packet-create-default" onClick={() => create(null)} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-1" />Start new packet</Button>
        {lib.length > 0 && <div className="flex gap-2">
          <select data-testid="packet-copy-from" className="border rounded-full h-10 px-3 text-sm bg-white" value={from} onChange={(e) => setFrom(e.target.value)}>
            <option value="">Copy from a past recital…</option>
            {lib.map((l) => <option key={l.production_id} value={l.production_id}>{l.production_name}</option>)}
          </select>
          <Button data-testid="packet-copy-button" variant="outline" disabled={!from} onClick={() => create(from)} className="rounded-full"><Copy className="w-4 h-4 mr-1" />Copy</Button>
        </div>}
      </div>
    </div>
  );
}

function OverviewFields({ draft, set }) {
  const f = (k, label, props = {}) => (
    <div className="space-y-1.5"><Label>{label}</Label><Input data-testid={`packet-${k}`} value={draft[k] || ""} onChange={(e) => set({ [k]: e.target.value })} {...props} /></div>);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 grid sm:grid-cols-2 gap-3" data-testid="packet-overview">
      <div className="sm:col-span-2">{f("title", "Packet title")}</div>
      {f("theme", "Recital theme", { placeholder: "e.g. Under the Stars" })}
      {f("performance_date", "Performance date", { type: "date" })}
      {f("performance_times", "Performance times", { placeholder: "e.g. 2:00 PM and 6:00 PM shows" })}
      {f("location", "Location")}
    </div>
  );
}

function Attachment({ draft, set }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy(true);
    try { set({ attachment: await uploadPacketFile(file) }); toast.success("PDF attached — save the draft to keep it"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="packet-attachment">
      <p className="font-head font-bold">Original PDF</p>
      <p className="text-xs text-muted-foreground mb-3">Upload last year's packet or a finished PDF as a reference, or offer it as an extra download. Its text isn't converted into sections.</p>
      {draft.attachment ? (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" data-view-ok onClick={() => openFile(draft.attachment)} data-testid="packet-attachment-open" className="text-sm font-semibold text-teal-dark underline">{draft.attachment.name}</button>
          <label className="flex items-center gap-2 text-sm ml-2"><input type="checkbox" data-testid="packet-attachment-downloadable" className="w-4 h-4 accent-teal" checked={!!draft.attachment_downloadable} onChange={(e) => set({ attachment_downloadable: e.target.checked })} />Offer to families as a download</label>
          <Button size="sm" variant="ghost" data-testid="packet-attachment-remove" className="rounded-full text-red-600" onClick={() => set({ attachment: null, attachment_downloadable: false })}>Remove</Button>
        </div>
      ) : (
        <label className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-gray-50">
          <Upload className="w-4 h-4" />{busy ? "Uploading…" : "Upload PDF"}
          <input type="file" accept="application/pdf" className="hidden" onChange={pick} data-testid="packet-attachment-input" />
        </label>
      )}
    </div>
  );
}

function Versions({ pid, refreshKey, title }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/packets/${pid}/versions`).then((r) => setRows(r.data)).catch(() => {}); }, [pid, refreshKey]);
  if (!rows.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="packet-versions">
      <p className="font-head font-bold flex items-center gap-2 mb-2"><History className="w-4 h-4 text-teal" />Published versions</p>
      <ul className="space-y-1.5 text-sm">{rows.map((v) => (
        <li key={v.version} className="flex items-center justify-between gap-2">
          <span>v{v.version} · {new Date(v.published_at).toLocaleString()} {v.published_by && <span className="text-muted-foreground">by {v.published_by}</span>}</span>
          <button type="button" data-view-ok data-testid={`packet-version-pdf-${v.version}`} className="text-teal-dark text-xs font-semibold underline" onClick={() => saveBlob(`/packets/${pid}/pdf?version=${v.version}`, `${title}-v${v.version}.pdf`)}>PDF</button>
        </li>))}</ul>
    </div>
  );
}

export function RecitalPacketTab({ pid }) {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState("");
  const [selected, setSelected] = useState(null);
  const [auto, setAuto] = useState(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [vKey, setVKey] = useState(0);
  const [pubDlg, setPubDlg] = useState(false);
  const [pubOpts, setPubOpts] = useState({ notify: false, message: "" });
  const [prep, setPrep] = useState(false);
  const load = async () => {
    const r = await api.get(`/packets/${pid}`);
    setState(r.data);
    if (r.data.draft) { setDraft(r.data.draft); setSaved(JSON.stringify(r.data.draft)); setSelected((s) => s || r.data.draft.sections[0]?.id || null); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setState(null); setDraft(null); setSelected(null); load().catch(() => setState({ packet: null })); api.get(`/packets/${pid}/auto`).then((r) => setAuto(r.data)).catch(() => {}); }, [pid]);
  const isDirty = !!draft && JSON.stringify(draft) !== saved;
  useEffect(() => {
    if (!isDirty) { return undefined; }
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);
  if (!state) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!state.packet) return <CreatePacket pid={pid} onCreated={load} />;
  if (!draft) return null;
  const dirty = JSON.stringify(draft) !== saved;
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setSections = (sections) => set({ sections });
  const pdfName = (draft.title || "recital-packet").replace(/[^\w -]/g, "").trim().replace(/\s+/g, "-");
  const saveDraft = async (quiet) => {
    setBusy(true);
    try { const r = await api.put(`/packets/${pid}`, draft); setDraft(r.data.draft); setSaved(JSON.stringify(r.data.draft)); setState((s) => ({ ...s, packet: { ...s.packet, has_unpublished_changes: r.data.has_unpublished_changes } })); if (!quiet) toast.success("Draft saved — families can't see drafts"); return true; }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save"); return false; } finally { setBusy(false); }
  };
  const publish = async () => {
    if (dirty && !(await saveDraft(true))) return;
    setPubDlg(true);
  };
  const doPublish = async () => {
    try { const r = await api.post(`/packets/${pid}/publish`, { notify: pubOpts.notify, message: pubOpts.message }); toast.success(`Published version ${r.data.version}${pubOpts.notify ? " — families are being notified" : ""}`); setPubDlg(false); const notified = pubOpts.notify; setPubOpts({ notify: false, message: "" }); await load(); setVKey((k) => k + 1); if (notified) { setTimeout(() => { load().catch(() => {}); }, 3000); } } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const unpublish = async () => {
    if (!window.confirm(dirty ? "Unpublish this packet? Your unsaved edits will be saved to the draft first." : "Unpublish this packet?")) return;
    if (dirty && !(await saveDraft(true))) return;
    try { await api.post(`/packets/${pid}/unpublish`); toast.success("Packet unpublished"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const discard = async () => {
    if (!window.confirm("Throw away all draft changes and go back to the published version?")) return;
    try { await api.post(`/packets/${pid}/discard-draft`); toast.success("Draft reset to the published version"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const downloadDraft = async () => { if (dirty && !(await saveDraft(true))) return; saveBlob(`/packets/${pid}/pdf?source=draft`, `${pdfName}-draft.pdf`); };
  const sec = draft.sections.find((s) => s.id === selected);
  const pk = state.packet;
  return (
    <div className="space-y-4" data-testid="recital-packet-tab">
      <div className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-wrap items-center gap-2 sticky top-2 z-10 shadow-sm">
        <StatusPill packet={pk} />
        {pk.has_unpublished_changes && pk.version > 0 && <span data-testid="packet-unpublished-changes" className="text-xs text-amber-700 flex items-center gap-1"><CircleDot className="w-3 h-3" />Draft has changes families won't see until you publish</span>}
        {dirty && <span data-testid="packet-dirty" className="text-xs text-red-600">Unsaved edits</span>}
        <span className="flex-1" />
        <Button size="sm" variant="outline" data-testid="packet-save" disabled={busy || !dirty} onClick={() => saveDraft(false)} className="rounded-full"><Save className="w-4 h-4 mr-1" />Save draft</Button>
        <Button size="sm" variant="outline" data-view-ok data-testid="packet-preview" onClick={() => setPreview(true)} className="rounded-full"><Eye className="w-4 h-4 mr-1" />Preview as parent</Button>
        <Button size="sm" variant="outline" data-view-ok data-testid="packet-pdf-draft" onClick={downloadDraft} className="rounded-full"><FileDown className="w-4 h-4 mr-1" />Draft PDF</Button>
        {pk.version > 0 && <Button size="sm" variant="outline" data-view-ok data-testid="packet-pdf-published" onClick={() => saveBlob(`/packets/${pid}/pdf?source=published`, `${pdfName}.pdf`)} className="rounded-full"><FileDown className="w-4 h-4 mr-1" />Published PDF</Button>}
        {pk.version > 0 && pk.has_unpublished_changes && <Button size="sm" variant="ghost" data-testid="packet-discard" onClick={discard} className="rounded-full"><Undo2 className="w-4 h-4 mr-1" />Discard draft</Button>}
        {pk.status === "published" && <Button size="sm" variant="ghost" data-testid="packet-unpublish" onClick={unpublish} className="rounded-full text-amber-700"><EyeOff className="w-4 h-4 mr-1" />Unpublish</Button>}
        <Button size="sm" data-testid="packet-publish" onClick={publish} className="rounded-full bg-teal hover:bg-teal-dark"><Send className="w-4 h-4 mr-1" />{pk.status === "published" && !pk.has_unpublished_changes && !dirty ? "Republish" : "Publish"}</Button>
      </div>
      <p className="text-xs text-muted-foreground">Families see only the published version in their Recital Hub. Drafts stay private until you publish.{pk.last_notified_at ? ` Families last notified ${new Date(pk.last_notified_at).toLocaleDateString()}.` : ""}</p>
      <OverviewFields draft={draft} set={set} />
      <div className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" data-testid="packet-checklist-enabled" className="w-4 h-4 accent-teal" checked={draft.checklist_enabled !== false} onChange={(e) => set({ checklist_enabled: e.target.checked })} />Show the parent preparation checklist</label>
        <span className="text-xs text-muted-foreground">Built from each dancer's published requirements. Takes effect when you publish.</span>
        <span className="flex-1" />
        <Button size="sm" variant="outline" data-view-ok data-testid="packet-prep-open" onClick={() => setPrep(true)} className="rounded-full"><ClipboardCheck className="w-4 h-4 mr-1" />Family readiness</Button>
      </div>
      {prep && <Dialog open onOpenChange={setPrep}><DialogContent className="bg-gray-50 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="packet-prep-dialog"><DialogHeader><DialogTitle className="font-head">Recital preparation overview</DialogTitle><DialogDescription>Which families are ready, and who may need a reminder.</DialogDescription></DialogHeader><PrepOverview pid={pid} /></DialogContent></Dialog>}
      <div className="grid lg:grid-cols-[300px_1fr] gap-4">
        <SectionList sections={draft.sections} setSections={setSections} selected={selected} setSelected={setSelected} />
        {sec ? <SectionEditor key={sec.id} section={sec} auto={auto} onChange={(patch) => setSections(draft.sections.map((s) => s.id === sec.id ? { ...s, ...patch } : s))} />
          : <EmptyState icon={BookOpen} title="Pick a section to edit" hint="Or add a custom section." />}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Attachment draft={draft} set={set} />
        <Versions pid={pid} refreshKey={vKey} title={pdfName} />
      </div>
      {pubDlg && <Dialog open onOpenChange={setPubDlg}>
        <DialogContent className="bg-white max-w-md" data-testid="packet-publish-dialog">
          <DialogHeader><DialogTitle className="font-head">Publish recital packet</DialogTitle><DialogDescription>Families will see this version in their Recital Hub. A new version is saved if anything changed.</DialogDescription></DialogHeader>
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" data-testid="packet-publish-notify" className="w-4 h-4 mt-0.5 accent-teal" checked={pubOpts.notify} onChange={(e) => setPubOpts({ ...pubOpts, notify: e.target.checked })} />Notify participating families by email and in-app notice</label>
          {pubOpts.notify && <textarea data-testid="packet-publish-message" rows={3} maxLength={1000} className="w-full border rounded-xl p-3 text-sm" placeholder="Optional note, e.g. 'Dress rehearsal time has changed'" value={pubOpts.message} onChange={(e) => setPubOpts({ ...pubOpts, message: e.target.value })} />}
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setPubDlg(false)}>Cancel</Button>
            <Button data-testid="packet-publish-confirm" className="rounded-full bg-teal hover:bg-teal-dark" onClick={doPublish}><Send className="w-4 h-4 mr-1" />Publish</Button>
          </div>
        </DialogContent>
      </Dialog>}
      {preview && <Dialog open onOpenChange={setPreview}>
        <DialogContent className="bg-gray-50 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="packet-preview-dialog">
          <DialogHeader><DialogTitle className="font-head">Parent preview</DialogTitle><DialogDescription>Exactly what families will see — this is your current {dirty ? "unsaved " : ""}draft.</DialogDescription></DialogHeader>
          <PacketView content={draft} auto={auto} onDownload={downloadDraft} onOpenFile={openFile} />
        </DialogContent>
      </Dialog>}
    </div>
  );
}
